import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';

import { TokenizedPaymentCard, SubmitOrderRequestPaymentMethod, SubmitOrderRequestPaymentMethodType } from '@/models';
import { sortFn, isDefined } from '@/helpers';
import { PaymentProviders, USPaymentProviders } from '@/constants';

@Component({
  name: 'CmgCheckoutCustomerPaymentMethods',
})
export default class CmgCheckoutCustomerPaymentMethods extends Vue {
  cardsBeingRemoved: string[] = [];
  isGiftCardBalanceLoading: boolean = false;

  get giftCard(): TokenizedPaymentCard | null {
    return this.$appStore.state.customer.wallet.paymentCards.find(card => !!card.isGiftCard) ?? null;
  }

  get errorMessage() {
    return this.giftCard && !this.isGiftCardBalanceLoading && this.getGiftCardBalance(this.giftCard) === null 
      ? 'Gift card balances are temporarily unavailable.  You may still attempt to pay with your gift card.'
      : '';
  }

  get availableCards(): TokenizedPaymentCard[] {
    const oneTimeCard = this.$appStore.state.checkout.payment.oneTimePaymentCard;
    const providerId = this.$appStore.state.checkout.paymentProvider;
    const isUSProvider = USPaymentProviders.includes(providerId);

    let cards = this.$appStore.state.customer.wallet.paymentCards
      .filter(card => 
        !isUSProvider 
          ? card.paymentProviderId == providerId 
          : USPaymentProviders.includes(card.paymentProviderId || ''))
      .sort(sortFn(card => !card.isGiftCard));
    if (oneTimeCard) {
      cards = [...cards, oneTimeCard];
    }
    return cards;
  }

  get orderTotal(): number {
    const order = this.$services.order.getCurrentOrder();
    if (!order) {
      return 0;
    }

    return order.order.orderTotalAmount;
  }

  get selectedCards(): SubmitOrderRequestPaymentMethod[] {
    return this.$appStore.state.checkout.payment.methods;
  }

  @Watch('availableCards', { immediate: true })
  async onAvailableCardsUpdated(cards: TokenizedPaymentCard[]) {
    // If there are available cards and we haven't selected any, select the first available
    if (cards.length && !this.selectedCards.length) {
      const card = cards.find(c => !this.isCardDisabled(c) && !c.isGiftCard);
      if (card) {
        this.onSelectCard(card);
      }
    }

    if (this.giftCard && this.getGiftCardBalance(this.giftCard) === null) {
      this.loadGiftCardBalance(this.giftCard);
    }
  }

  isCardSelected(card: TokenizedPaymentCard): boolean {
    const cardKey = this.getCardKey(card);
    return this.selectedCards.some(p => cardKey == this.getPaymentMethodKey(p, !!card.isGiftCard));
  }

  getCardKey(card: TokenizedPaymentCard): string {
    return card.tokenId?.toString() ?? card.tokenizedAccountNumber;
  }

  getPaymentMethodKey(paymentMethod: SubmitOrderRequestPaymentMethod, isGiftCard: boolean): string {
    return paymentMethod.chipotleWalletId?.toString() ?? (isGiftCard ? paymentMethod.giftCardNumber : paymentMethod.creditCardSingleUseToken) ?? '';
  }

  lastFourOfCardNumber(card: TokenizedPaymentCard): string {
    const number = card.tokenizedAccountNumber;

    return number.slice(number.length - 4);
  }

  getGiftCardBalance(card: TokenizedPaymentCard): number | null {
    if (!card.isGiftCard) {
      return null;
    }
    const balance = this.$appStore.state.customer.wallet.giftCardBalances[card.tokenizedAccountNumber];
    return isDefined(balance) ? balance : null;
  }

  formatPrice(price: number): string {
    return this.$services.utils.formatPrice(price);
  }

  isCardDisabled(card: TokenizedPaymentCard): boolean {
    return card.isGiftCard ? this.isGiftCardDisabled(card) : this.isCreditOrDebitCardDisabled(card);
  }

  isCardBeingRemoved(card: TokenizedPaymentCard) {
    return !!this.cardsBeingRemoved.find(c => c == card.tokenizedAccountNumber);
  }

  onSelectCard(card: TokenizedPaymentCard) {
    let selected: SubmitOrderRequestPaymentMethod[] = [];

    if (card.isGiftCard) {
      const giftCardBalance = this.getGiftCardBalance(card);
      // If the balance is unknown or the balance is enough to cover the whole order total then treated it like a full payment.
      const partialPaymentNotNeeded = giftCardBalance === null || giftCardBalance >= this.orderTotal;
      if (this.isCardSelected(card)) {
        // Only allow deselecting a gift card if partial payment is required
        selected = partialPaymentNotNeeded ? this.selectedCards : this.selectedCards.filter(payment => payment.giftCardNumber !== card.tokenizedAccountNumber);
      } else {
        // Not selected payment method
        const giftCard = this.mapPaymentCardToMethod(card);
        if (partialPaymentNotNeeded) {
          selected.push(giftCard);
        } else {
          // Set gift card balance difference from order total.
          giftCard.amount = giftCardBalance as number;
          // Append the gift card and replace the entire store state.
          selected.push(giftCard);
          if (this.selectedCards.length > 0) {
            const cc = cloneDeep(this.selectedCards[0]);
            cc.amount = parseFloat((this.orderTotal - giftCard.amount).toFixed(2));
            selected.push(cc);
          }
        }
      }
    } else {
      const creditCard = this.mapPaymentCardToMethod(card);
      const selectedGiftCard = this.selectedCards.find(payment => payment.paymentType === SubmitOrderRequestPaymentMethodType.ChipotleWalletGiftCard);

      if (selectedGiftCard && selectedGiftCard.amount) {
        creditCard.amount = parseFloat((this.orderTotal - selectedGiftCard.amount).toFixed(2));
        selected.push(selectedGiftCard, creditCard);
      } else {
        selected.push(creditCard);
      }
    }

    this.$services.checkout.setPaymentMethods(selected);
  }

  async onRemoveCard(card: TokenizedPaymentCard) {
    this.cardsBeingRemoved.push(card.tokenizedAccountNumber);
    if (card.isOneTimePaymentCard) {
      this.$services.checkout.setOneTimePayCard(null);
      this.$services.checkout.setPaymentMethods([]);
    } else {
      await this.$services.transaction.deletePaymentCard(card.tokenId || 0);
    }
    await this.$services.transaction.refreshPaymentCards();

    if (card.isGiftCard) {
      this.$services.transaction.clearGiftCardBalanceCache();
    }

    this.cardsBeingRemoved = this.cardsBeingRemoved.filter(c => c !== card.tokenizedAccountNumber);
  }

  private isGiftCardDisabled(card: TokenizedPaymentCard) {
    const balance = this.getGiftCardBalance(card);
    return this.isGiftCardBalanceLoading || balance === 0;
  }

  private isCreditOrDebitCardDisabled(card: TokenizedPaymentCard) {
    const today = moment();
    const year = parseInt(today.format('YY'), 10);

    const expYear = parseInt(card.expirationYear || '', 10);
    const expMonth = parseInt(card.expirationMonth || '', 10);

    if (isNaN(expYear) || isNaN(expMonth)) {
      return true;
    }

    if (expYear > year) {
      return false;
    } else if (expYear < year) {
      return true;
    }

    return expMonth <= today.get('month');
  }

  private mapPaymentCardToMethod(card: TokenizedPaymentCard): SubmitOrderRequestPaymentMethod {
    const method: SubmitOrderRequestPaymentMethod = {
      cardHolderName: card.cardHolderName,
      creditCardSingleUseToken: !card.isGiftCard ? card.tokenizedAccountNumber : '',
      chipotleWalletId: card.tokenId,
      creditCardType: card.paymentMethod.replace(/-/g, ' '),
      creditCardExpiration: card.expirationMonth && card.expirationYear ? `${card.expirationMonth}${card.expirationYear}` : '',
      creditCardZipCode: card.billingZip,
      paymentType: card.isGiftCard ? SubmitOrderRequestPaymentMethodType.ChipotleWalletGiftCard : (card.isOneTimePaymentCard ? SubmitOrderRequestPaymentMethodType.CreditCard : SubmitOrderRequestPaymentMethodType.ChipotleWalletCreditCard),
      paymentProviderId: card.paymentProviderId,
      lastFourAccountNumbers: card.lastFourAccountNumbers,
    };

    if (card.isGiftCard) {
      method.giftCardNumber = card.tokenizedAccountNumber;
    }

    return method;
  }

  private async loadGiftCardBalance(giftCard: TokenizedPaymentCard): Promise<void> {
    this.isGiftCardBalanceLoading = true;
    try {
      await this.$services.transaction.getGiftCardBalance(giftCard.tokenizedAccountNumber);
    } 
    // tslint:disable-next-line:no-empty
    /* eslint no-empty: ["error", { "allowEmptyCatch": true }] */
    catch {}
    finally {
      this.isGiftCardBalanceLoading = false;
    }
  }
}
